import type { ReactNode } from 'react';
import { useState } from 'react';
import type { Activity } from '../../types';
import { useActivities, getActivityTypeIcon, formatDueDate, formatDueTime } from '../../hooks/useActivities';

export interface ActivityTimelineProps {
  parentType: 'lead' | 'opportunity';
  parentId: string;
  limit?: number;
  showAddButton?: boolean;
}

interface ActivityItemProps {
  activity: Activity;
  isOverdue?: boolean;
  onComplete: (activityId: string, outcome?: string) => Promise<boolean>;
  onReschedule: (activityId: string) => void;
}

interface RescheduleModalProps {
  activity: Activity;
  onReschedule: (activityId: string, dueDate: string, reason?: string) => Promise<boolean>;
  onClose: () => void;
}

interface NewActivityModalProps {
  parentType: 'lead' | 'opportunity';
  parentId: string;
  onCreate: (activity: Parameters<ReturnType<typeof useActivities>['createActivity']>[0]) => Promise<Activity | null>;
  onClose: () => void;
}

function ActivityItem({ activity, isOverdue, onComplete, onReschedule }: ActivityItemProps): ReactNode {
  const [completing, setCompleting] = useState(false);
  const [showOutcomeInput, setShowOutcomeInput] = useState(false);
  const [outcomeText, setOutcomeText] = useState('');

  const isCompleted = activity.status === 'completed';

  const handleComplete = async () => {
    if (activity.type === 'call' || activity.type === 'meeting') {
      setShowOutcomeInput(true);
    } else {
      setCompleting(true);
      await onComplete(activity.id);
      setCompleting(false);
    }
  };

  const handleCompleteWithOutcome = async () => {
    setCompleting(true);
    await onComplete(activity.id, outcomeText);
    setCompleting(false);
    setShowOutcomeInput(false);
    setOutcomeText('');
  };

  return (
    <div
      className={`py-3 flex items-start gap-3 border-b border-gray-100 last:border-b-0 ${
        isOverdue ? 'bg-red-50 -mx-4 px-4 rounded-md' : ''
      }`}
    >
      {/* Type Icon */}
      <span className="text-xl flex-shrink-0 mt-0.5" title={activity.type}>
        {getActivityTypeIcon(activity.type)}
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`font-medium ${
              isCompleted ? 'text-gray-400 line-through' : 'text-gray-900'
            }`}
          >
            {activity.subject}
          </span>
          {isOverdue && !isCompleted && (
            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
              Overdue
            </span>
          )}
        </div>

        {/* Due date/time */}
        {activity.due_date && !isCompleted && (
          <div className={`text-sm mt-0.5 ${isOverdue ? 'text-red-600' : 'text-gray-500'}`}>
            {formatDueDate(activity.due_date)}
            {activity.due_time && ` at ${formatDueTime(activity.due_time)}`}
          </div>
        )}

        {/* Completed info */}
        {isCompleted && activity.completed_at && (
          <div className="text-sm text-gray-400 mt-0.5">
            Completed {formatDueDate(activity.completed_at)}
          </div>
        )}

        {/* Outcome notes */}
        {isCompleted && activity.outcome_notes && (
          <div className="text-sm text-gray-500 mt-1 italic">
            "{activity.outcome_notes}"
          </div>
        )}

        {/* Assignee */}
        {activity.assigned_to && (
          <div className="text-xs text-gray-400 mt-1">
            Assigned to: {activity.assigned_to}
          </div>
        )}

        {/* Outcome input for calls/meetings */}
        {showOutcomeInput && (
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              value={outcomeText}
              onChange={(e) => setOutcomeText(e.target.value)}
              placeholder="How did it go? (optional)"
              className="flex-1 text-sm px-2 py-1"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCompleteWithOutcome();
                if (e.key === 'Escape') setShowOutcomeInput(false);
              }}
            />
            <button
              onClick={handleCompleteWithOutcome}
              disabled={completing}
              className="btn btn-primary text-xs px-2 py-1"
            >
              {completing ? '...' : 'Done'}
            </button>
            <button
              onClick={() => setShowOutcomeInput(false)}
              className="btn btn-secondary text-xs px-2 py-1"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Actions */}
      {!isCompleted && !showOutcomeInput && (
        <div className="flex gap-1 flex-shrink-0">
          <button
            onClick={handleComplete}
            disabled={completing}
            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors"
            title="Mark complete"
          >
            {completing ? (
              <span className="text-sm">...</span>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
          <button
            onClick={() => onReschedule(activity.id)}
            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
            title="Reschedule"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

function RescheduleModal({ activity, onReschedule, onClose }: RescheduleModalProps): ReactNode {
  const [dueDate, setDueDate] = useState(
    activity.due_date ? activity.due_date.split('T')[0] : ''
  );
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dueDate) return;

    setSubmitting(true);
    const success = await onReschedule(activity.id, dueDate, reason || undefined);
    setSubmitting(false);

    if (success) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Reschedule Activity
        </h3>
        <p className="text-sm text-gray-600 mb-4">{activity.subject}</p>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Date
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason (optional)
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you rescheduling?"
              className="w-full"
            />
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !dueDate}
              className="btn btn-primary"
            >
              {submitting ? 'Saving...' : 'Reschedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NewActivityModal({ parentType, parentId, onCreate, onClose }: NewActivityModalProps): ReactNode {
  const [type, setType] = useState<Activity['type']>('task');
  const [subject, setSubject] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim()) return;

    setSubmitting(true);
    const activity = await onCreate({
      type,
      subject: subject.trim(),
      description: description.trim() || undefined,
      due_date: dueDate || undefined,
      [parentType === 'lead' ? 'lead_id' : 'opportunity_id']: parentId,
    });
    setSubmitting(false);

    if (activity) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          New Activity
        </h3>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as Activity['type'])}
              className="w-full"
            >
              <option value="call">📞 Call</option>
              <option value="email">✉️ Email</option>
              <option value="meeting">📅 Meeting</option>
              <option value="task">✅ Task</option>
              <option value="note">📝 Note</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="What needs to be done?"
              className="w-full"
              required
              autoFocus
            />
          </div>

          {type !== 'note' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full"
              />
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add details..."
              className="w-full h-20 resize-none"
            />
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !subject.trim()}
              className="btn btn-primary"
            >
              {submitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface ActivityGroupProps {
  title: string;
  activities: Activity[];
  isOverdue?: boolean;
  defaultExpanded?: boolean;
  onComplete: (activityId: string, outcome?: string) => Promise<boolean>;
  onReschedule: (activityId: string) => void;
}

function ActivityGroup({
  title,
  activities,
  isOverdue,
  defaultExpanded = true,
  onComplete,
  onReschedule,
}: ActivityGroupProps): ReactNode {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (activities.length === 0) return null;

  return (
    <div className="mb-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors w-full text-left mb-2"
      >
        <svg
          className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className={isOverdue ? 'text-red-600' : ''}>{title}</span>
        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
          isOverdue ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
        }`}>
          {activities.length}
        </span>
      </button>

      {expanded && (
        <div className="ml-6">
          {activities.map((activity) => (
            <ActivityItem
              key={activity.id}
              activity={activity}
              isOverdue={isOverdue}
              onComplete={onComplete}
              onReschedule={onReschedule}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function ActivityTimeline({
  parentType,
  parentId,
  limit,
  showAddButton = true,
}: ActivityTimelineProps): ReactNode {
  const {
    grouped,
    loading,
    error,
    completeActivity,
    rescheduleActivity,
    createActivity,
  } = useActivities({ parentType, parentId, limit });

  const [rescheduleActivityId, setRescheduleActivityId] = useState<string | null>(null);
  const [showNewActivity, setShowNewActivity] = useState(false);

  const activityToReschedule =
    rescheduleActivityId
      ? [
          ...grouped.overdue,
          ...grouped.today,
          ...grouped.upcoming,
        ].find((a) => a.id === rescheduleActivityId)
      : null;

  const handleReschedule = (activityId: string) => {
    setRescheduleActivityId(activityId);
  };

  if (loading) {
    return (
      <div className="card">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-12 bg-gray-100 rounded"></div>
            <div className="h-12 bg-gray-100 rounded"></div>
            <div className="h-12 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="text-red-600 text-sm">{error}</div>
      </div>
    );
  }

  const hasActivities =
    grouped.overdue.length > 0 ||
    grouped.today.length > 0 ||
    grouped.upcoming.length > 0 ||
    grouped.completed.length > 0;

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-gray-900">Activities</h2>
        {showAddButton && (
          <button
            onClick={() => setShowNewActivity(true)}
            className="btn btn-primary text-sm px-3 py-1.5"
          >
            + Add
          </button>
        )}
      </div>

      {!hasActivities ? (
        <div className="text-center py-8">
          <p className="text-gray-500 text-sm mb-4">No activities yet</p>
          {showAddButton && (
            <button
              onClick={() => setShowNewActivity(true)}
              className="btn btn-secondary text-sm"
            >
              Create Activity
            </button>
          )}
        </div>
      ) : (
        <>
          <ActivityGroup
            title="Overdue"
            activities={grouped.overdue}
            isOverdue
            onComplete={completeActivity}
            onReschedule={handleReschedule}
          />

          <ActivityGroup
            title="Today"
            activities={grouped.today}
            onComplete={completeActivity}
            onReschedule={handleReschedule}
          />

          <ActivityGroup
            title="Upcoming"
            activities={grouped.upcoming}
            onComplete={completeActivity}
            onReschedule={handleReschedule}
          />

          <ActivityGroup
            title="Completed"
            activities={grouped.completed}
            defaultExpanded={false}
            onComplete={completeActivity}
            onReschedule={handleReschedule}
          />
        </>
      )}

      {/* Reschedule Modal */}
      {activityToReschedule && (
        <RescheduleModal
          activity={activityToReschedule}
          onReschedule={rescheduleActivity}
          onClose={() => setRescheduleActivityId(null)}
        />
      )}

      {/* New Activity Modal */}
      {showNewActivity && (
        <NewActivityModal
          parentType={parentType}
          parentId={parentId}
          onCreate={createActivity}
          onClose={() => setShowNewActivity(false)}
        />
      )}
    </div>
  );
}

export default ActivityTimeline;
